/**
 * Approved-pay edit locks and the paid-pay warning (api#1321, api#1352).
 *
 * A ticket whose crew pay is APPROVED refuses a change to its dates or to a
 * pay-affecting line with a 409:
 *
 *     { success: false, code: 'pay_approved_lines_locked' | 'pay_approved_dates_locked',
 *       error: <sentence>, details: { payPeriodStatus: 'open' | 'approved', periods: [...] } }
 *
 * The sentence already says what to do. On an open period it is "the office
 * must unapprove pay"; on an approved period it is "reopen the pay period, then
 * unapprove pay". The screen shows `error` verbatim and never builds its own
 * remedy from `payPeriodStatus`. A second wording here would be a second answer
 * to one question, and it would be the one that goes stale.
 *
 * A ticket whose pay is already EXPORTED or PAID saves anyway, and the 200 body
 * carries `warnings: [{ code: 'pay_already_paid', message }]`. That is a saved
 * record plus an instruction (use a payroll adjustment), so it is shown as a
 * warning and never as a success tick.
 *
 * `/sync/push` answers both the same way: a refusal is a conflict carrying
 * `code` and `reason` (the same sentence), and an applied mutation may carry
 * the same `warnings[]`.
 */

export const PAY_APPROVED_LINES_LOCKED = 'pay_approved_lines_locked';
export const PAY_APPROVED_DATES_LOCKED = 'pay_approved_dates_locked';
export const PAY_ALREADY_PAID = 'pay_already_paid';

const LOCK_CODES: ReadonlySet<string> = new Set([PAY_APPROVED_LINES_LOCKED, PAY_APPROVED_DATES_LOCKED]);

/** Is this response body one of the approved-pay edit-lock refusals? Keyed on `code`, never on the wording. */
export function isPayLockRefusal(body: unknown): boolean {
  const code = (body as any)?.code;
  return typeof code === 'string' && LOCK_CODES.has(code);
}

export interface PayWarning {
  code: string;
  message: string;
}

/**
 * The `warnings[]` on a save response, keeping only entries that carry both a
 * code and a sentence. Absent or malformed reads as "no warnings". That is
 * safe here because a warning never changes what was saved, only what the
 * reader is told about it.
 */
export function readPayWarnings(body: unknown): PayWarning[] {
  const raw = (body as any)?.warnings;
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (w: any): w is PayWarning =>
      typeof w?.code === 'string' && w.code.length > 0
      && typeof w?.message === 'string' && w.message.length > 0,
  );
}

/** The paid-pay warning's sentence, if the body carries one. */
export function payAlreadyPaidMessage(body: unknown): string | null {
  return readPayWarnings(body).find(w => w.code === PAY_ALREADY_PAID)?.message ?? null;
}
